// ============================================
// Admin - Landing Page Signups
// ============================================

import React from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { LandingSignUp } from '../../types';

const STATUS_OPTIONS = ['new', 'contacted', 'converted', 'closed'] as const;

const STATUS_COLORS: Record<string, string> = {
  new: '#22c55e',
  contacted: '#3b82f6',
  converted: '#8b5cf6',
  closed: '#6b7280',
};

const ROLE_LABELS: Record<string, string> = {
  organizer: 'Tournament Organizer',
  massage_team: 'Massage Team',
  venue: 'Venue / Casino',
  other: 'Other',
};

export default function Signups() {
  const { role } = useAuth();
  const [signups, setSignups] = React.useState<LandingSignUp[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [editingNotes, setEditingNotes] = React.useState<string | null>(null);
  const [notesValue, setNotesValue] = React.useState('');

  const isSuperAdmin = role === 'superadmin';

  // Load signups (only for superadmin)
  React.useEffect(() => {
    if (!isSuperAdmin) return;

    const q = query(collection(db, 'signups'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as LandingSignUp[];
      setSignups(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isSuperAdmin]);

  // Only superadmin can access this
  if (!isSuperAdmin) {
    return (
      <div className="admin-page">
        <h1>Access Denied</h1>
        <p>Only superadmin can view signups.</p>
      </div>
    );
  }

  const updateStatus = async (signupId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'signups', signupId), {
        status: newStatus,
      });
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const saveNotes = async (signupId: string) => {
    try {
      await updateDoc(doc(db, 'signups', signupId), {
        notes: notesValue,
      });
      setEditingNotes(null);
    } catch (err) {
      console.error('Error saving notes:', err);
    }
  };

  const formatDate = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-page">
        <h1>Signups</h1>
        <p>Loading...</p>
      </div>
    );
  }

  const newCount = signups.filter((s) => s.status === 'new').length;

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>
          Landing Page Signups
          {newCount > 0 && (
            <span style={{
              marginLeft: '0.75rem',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#22c55e',
              color: '#fff',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              {newCount} new
            </span>
          )}
        </h1>
      </div>

      {signups.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '12px',
          color: 'var(--color-text-secondary)',
        }}>
          No signups yet. They will appear here when visitors submit the form on the landing page.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {signups.map((signup) => (
            <div
              key={signup.id}
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: '12px',
                overflow: 'hidden',
                borderLeft: `4px solid ${STATUS_COLORS[signup.status]}`,
              }}
            >
              {/* Header row - always visible */}
              <div
                onClick={() => setExpandedId(expandedId === signup.id ? null : signup.id)}
                style={{
                  padding: '1rem',
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr auto',
                  gap: '1rem',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{signup.name}</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                    {signup.email}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem' }}>{signup.organization}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                    {ROLE_LABELS[signup.role] || signup.role}
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                  {formatDate(signup.createdAt)}
                </div>
                <select
                  value={signup.status}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateStatus(signup.id, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-bg)',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Expanded details */}
              {expandedId === signup.id && (
                <div style={{
                  padding: '1rem',
                  paddingTop: 0,
                  borderTop: '1px solid var(--color-border)',
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                        Phone
                      </div>
                      <div>{signup.phone || 'Not provided'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                        Email
                      </div>
                      <a href={`mailto:${signup.email}`} style={{ color: '#3b82f6' }}>
                        {signup.email}
                      </a>
                    </div>
                  </div>

                  {signup.message && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                        Message
                      </div>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--color-bg)',
                        borderRadius: '6px',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {signup.message}
                      </div>
                    </div>
                  )}

                  {/* Notes section */}
                  <div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '0.25rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <span>Internal Notes</span>
                      {editingNotes !== signup.id && (
                        <button
                          onClick={() => {
                            setEditingNotes(signup.id);
                            setNotesValue(signup.notes || '');
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.75rem',
                            backgroundColor: 'transparent',
                            border: '1px solid var(--color-border)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingNotes === signup.id ? (
                      <div>
                        <textarea
                          value={notesValue}
                          onChange={(e) => setNotesValue(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            backgroundColor: 'var(--color-bg)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                            color: 'var(--color-text)',
                            resize: 'vertical',
                            marginBottom: '0.5rem',
                          }}
                          placeholder="Add internal notes..."
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => saveNotes(signup.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#22c55e',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingNotes(null)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: 'transparent',
                              color: 'var(--color-text-secondary)',
                              border: '1px solid var(--color-border)',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--color-bg)',
                        borderRadius: '6px',
                        color: signup.notes ? 'var(--color-text)' : 'var(--color-text-tertiary)',
                        fontStyle: signup.notes ? 'normal' : 'italic',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {signup.notes || 'No notes yet'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
